import { createTheme } from '@mui/material/styles';

const FONT_STACK = 'Inter, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

const theme = createTheme({
  spacing: 4,
  palette: {
    primary: { main: '#292929', contrastText: '#ffffff' },
    secondary: { main: '#006666' },
    success: { main: '#237a45' },
    warning: { main: '#b65f00' },
    error: { main: '#b42318' },
    info: { main: '#1769aa' },
    background: { default: '#f0f0f0', paper: '#ffffff' },
    text: { primary: '#1f1f1f', secondary: '#525252' },
    divider: '#d6d6d6',
  },
  typography: {
    fontFamily: FONT_STACK,
    fontSize: 14,
    h1: { fontSize: '1.5rem', lineHeight: 1.25, fontWeight: 600 },
    h2: { fontSize: '1.375rem', lineHeight: 1.3, fontWeight: 600 },
    h3: { fontSize: '1.125rem', lineHeight: 1.35, fontWeight: 600 },
    h4: { fontSize: '1rem', lineHeight: 1.4, fontWeight: 600 },
    h5: { fontSize: '0.9375rem', lineHeight: 1.4, fontWeight: 600 },
    h6: { fontSize: '0.875rem', lineHeight: 1.4, fontWeight: 600 },
    body1: { fontSize: '0.875rem' },
    body2: { fontSize: '0.8125rem' },
    caption: { fontSize: '0.75rem' },
    button: { textTransform: 'none', fontWeight: 600, fontSize: '0.8125rem' },
  },
  shape: { borderRadius: 6 },
  zIndex: {
    mobileStepper: 100,
    fab: 100,
    speedDial: 100,
    appBar: 1000,
    drawer: 900,
    modal: 1410,
    snackbar: 1500,
    tooltip: 1510,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        'html, body, #root': {
          height: '100%',
          overflow: 'hidden',
          fontFamily: FONT_STACK,
          backgroundColor: '#f0f0f0',
        },
      },
    },
    MuiContainer: {
      defaultProps: { maxWidth: false, disableGutters: true },
      styleOverrides: {
        root: {
          width: '100%',
          maxWidth: '100% !important',
          paddingLeft: 0,
          paddingRight: 0,
        },
      },
    },
    MuiPaper: {
      styleOverrides: { root: { backgroundImage: 'none' } },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          border: '1px solid #d6d6d6',
          borderRadius: 8,
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.06)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          minHeight: 36,
          borderRadius: 5,
          padding: '6px 14px',
          boxShadow: 'none',
          '&.MuiButton-sizeSmall': { minHeight: 30, padding: '4px 10px' },
          '&.MuiButton-sizeLarge': { minHeight: 40 },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 5,
          '&.MuiIconButton-sizeSmall': { width: 30, height: 30 },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          minHeight: 36,
          borderRadius: 5,
          backgroundColor: '#ffffff',
          fontSize: 14,
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#006666',
            boxShadow: '0 0 0 3px rgba(0, 102, 102, 0.16)',
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: { fontSize: 13, '&.Mui-focused': { color: '#006666' } },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          maxHeight: 'calc(100vh - 48px)',
          border: '1px solid #d6d6d6',
          borderRadius: 8,
          boxShadow: '0 18px 50px rgba(0, 0, 0, 0.2)',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: '16px 20px',
          borderBottom: '1px solid #d6d6d6',
          fontSize: 17,
          fontWeight: 600,
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: { root: { padding: 20 } },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          gap: 8,
          padding: '12px 20px',
          borderTop: '1px solid #d6d6d6',
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          border: '1px solid #d6d6d6',
          borderRadius: 6,
          boxShadow: '0 10px 28px rgba(0, 0, 0, 0.14)',
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        // Menus/selects are popovers; they must sit above dialogs (modal: 1410) or a dropdown
        // opened inside a dialog renders behind it. Still below snackbar/toast (1500).
        root: { zIndex: 1420 },
      },
    },
    MuiMenuItem: {
      styleOverrides: { root: { minHeight: 34, fontSize: 13 } },
    },
    MuiTabs: {
      styleOverrides: {
        root: { minHeight: 38, borderBottom: '1px solid #d6d6d6' },
        indicator: { height: 2, backgroundColor: '#292929' },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          minWidth: 0,
          minHeight: 38,
          padding: '8px 14px',
          color: '#525252',
          fontSize: 13,
          fontWeight: 500,
          textTransform: 'none',
          '&.Mui-selected': { color: '#1f1f1f', fontWeight: 600 },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { height: 22, borderRadius: 999, fontSize: 11, fontWeight: 600 },
      },
    },
    MuiTooltip: {
      defaultProps: { enterDelay: 350, arrow: true },
      styleOverrides: {
        tooltip: { borderRadius: 4, backgroundColor: '#1f1f1f', fontSize: 12 },
        arrow: { color: '#1f1f1f' },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-root': {
            backgroundColor: '#f7f7f7',
            color: '#525252',
            fontSize: 12,
            fontWeight: 700,
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: '10px 12px',
          borderBottomColor: '#e3e3e3',
          fontSize: 13,
        },
      },
    },
  },
});

export default theme;
